import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAstriaCopy, CopyKey } from "./copy";

const stroke = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const I = {
  today: <svg {...stroke}><circle cx="12" cy="12" r="4" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" /></svg>,
  gps: <svg {...stroke}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></svg>,
  astro: <svg {...stroke}><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" /></svg>,
  ask: <svg {...stroke}><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /></svg>,
  journey: <svg {...stroke}><path d="M5 19c0-6 14-2 14-12" /><circle cx="5" cy="19" r="1.5" /><circle cx="19" cy="7" r="1.5" /></svg>,
  account: <svg {...stroke}><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></svg>,
};

const PRIMARY: { to: string; label: CopyKey; icon: React.ReactNode }[] = [
  { to: "/today", label: "navToday", icon: I.today },
  { to: "/lifeGps", label: "navLifeGps", icon: I.gps },
  { to: "/astrology", label: "navAstrology", icon: I.astro },
  { to: "/sanctuary", label: "navAsk", icon: I.ask },
  { to: "/journey-story", label: "navJourney", icon: I.journey },
];


const itemClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 px-4 py-3 text-[15px] transition ${
    isActive ? "text-astria-text" : "text-astria-muted hover:bg-white/5"
  }`;

const itemStyle = (isActive: boolean): React.CSSProperties => ({
  borderRadius: 12,
  background: isActive ? "var(--astria-accent-glow)" : undefined,
  boxShadow: isActive ? "inset 2px 0 0 var(--astria-lang-tint)" : undefined,
});

/** Five calm places + a quiet account menu. No flags, no emoji. */
const AstriaNav: React.FC<{ collapsed?: boolean; compact?: boolean }> = ({ collapsed = false, compact = false }) => {
  const { pathname } = useLocation();
  const inAccount = pathname.startsWith("/account");
  const c = useAstriaCopy();

  if (compact) {
    // One slim icon row: keeps the five places (and Account) reachable while History is open.
    const items = [...PRIMARY, { to: "/account/profile", label: "navAccount" as CopyKey, icon: I.account }];
    return (
      <nav aria-label="Astria" className="grid grid-cols-6 gap-1 px-1 pb-2">
        {items.map((p) => {
          const on = p.to === "/account/profile" ? inAccount : pathname === p.to;
          return (
            <NavLink
              key={p.to}
              to={p.to}
              title={c(p.label)}
              aria-label={c(p.label)}
              className={() =>
                `flex h-10 items-center justify-center transition ${
                  on ? "text-astria-text" : "text-astria-muted hover:bg-white/5"
                }`
              }
              style={() => itemStyle(on)}
            >
              {p.icon}
            </NavLink>
          );
        })}
      </nav>
    );
  }

  if (collapsed) {
    // Icon-only rail for the collapsed sidebar: the five places stay one tap away.
    return (
      <nav aria-label="Astria" className="flex flex-col items-center gap-1 pt-3">
        {[...PRIMARY, { to: "/account/profile", label: "navAccount" as CopyKey, icon: I.account }].map((p) => (
          <NavLink
            key={p.to}
            to={p.to}
            title={c(p.label)}
            aria-label={c(p.label)}
            className={({ isActive }) =>
              `flex h-11 w-11 items-center justify-center transition ${
                isActive ? "text-astria-text" : "text-astria-muted hover:bg-white/5"
              }`
            }
            style={({ isActive }) => itemStyle(isActive)}
          >
            {p.icon}
          </NavLink>
        ))}
        <div className="my-2 h-px w-8 bg-white/10" />
      </nav>
    );
  }

  return (
    <nav aria-label="Astria" className="space-y-1 px-1">
      {PRIMARY.map((p) => (
        <NavLink key={p.to} to={p.to} className={itemClass} style={({ isActive }) => itemStyle(isActive)}>
          {p.icon}
          {c(p.label)}
        </NavLink>
      ))}

      <NavLink
        to="/account/profile"
        className={() => itemClass({ isActive: inAccount })}
        style={() => itemStyle(inAccount)}
      >
        {I.account}
        {c("navAccount")}
      </NavLink>
      <div className="mx-2 my-3 h-px bg-white/10" />
    </nav>
  );
};

export default AstriaNav;
